import React, { useState, useRef, useCallback, useEffect } from 'react';
import T from '../styles/theme';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import { fmtDollar } from './UI';
import Icon from './Icon';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const synth = window.speechSynthesis;

function speak(text) {
  if (!synth) return;
  synth.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 1.0;
  u.pitch = 1;
  u.volume = 0.9;
  const voices = synth.getVoices();
  // Ranked preference: best natural voices first
  const preferred = [
    "Google UK English Male", "Google UK English Female",
    "Daniel", "Aaron", "Evan", "Rishi",
    "Samantha", "Karen", "Moira", "Tessa",
    "Google US English", "Alex",
  ];
  let voice = null;
  for (const name of preferred) {
    voice = voices.find(v => v.name.includes(name));
    if (voice) break;
  }
  if (!voice) voice = voices.find(v => v.lang === "en-US" && v.localService) || voices[0];
  if (voice) u.voice = voice;
  synth.speak(u);
}

function findContact(contacts, query) {
  const q = query.toLowerCase().trim();
  let match = contacts.find(c => (c.fn + " " + c.ln).toLowerCase() === q);
  if (match) return match;
  match = contacts.find(c => c.fn?.toLowerCase() === q);
  if (match) return match;
  match = contacts.find(c => c.ln?.toLowerCase() === q);
  if (match) return match;
  match = contacts.find(c => (c.fn + " " + c.ln).toLowerCase().includes(q));
  if (match) return match;
  match = contacts.find(c => (c.co || c.company || "").toLowerCase().includes(q));
  return match || null;
}

/* ── LOCAL COMMAND PARSER (fast fallback) ── */
function parseCommandLocal(transcript, store, actions) {
  // Mission Control has its own command processor — never process here
  if (window.__coreflow_current_page === "dashboard") return null;
  const t = transcript.toLowerCase().trim();
  const has = (...words) => words.some(w => t.includes(w));
  const extractName = () => {
    return t.replace(/\b(can you |please |could you |i want to |i need to |let me |go ahead and |give |give me |a call|a breakdown|for me|right now|to |call |dial |ring |phone |show |open |find |pull up |look up |email |message |text |about |tell me about |who is |what about |info on |details on |breakdown of |breakdown on |what do you know about |everything on |everything about )\b/gi, "").trim();
  };

  // Greetings
  if (/^(hey|hi|hello|what's up|good morning|good afternoon|good evening|yo|sup)\b/i.test(t) && t.split(" ").length < 6) {
    const hour = new Date().getHours();
    const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    const activeTasks = store.tasks.filter(t => !t.done);
    const open = store.deals.filter(d => d.stage !== "Closed Won");
    const risky = open.filter(d => (d.risk || "none") === "high" || d.days > 14);
    return `${greeting}! ${open.length} open deals, ${activeTasks.length} tasks. ${risky.length > 0 ? risky.length + " deals need attention." : "Pipeline looks healthy."} What can I help with?`;
  }

  // Help
  if (has("help", "what can you do", "commands")) {
    return "I can do a lot. Try: call Sarah Chen, show my deals, who should I call next, how's my pipeline, what's my next task, any deals at risk, biggest deal, how many contacts. Just talk naturally.";
  }

  // Navigation
  if (has("dashboard", "command center", "home page")) { actions.navigate("dashboard"); return "Opening the command center."; }
  if (has("contacts page", "contact list", "people page", "show people", "show contacts")) { actions.navigate("contacts"); return "Here are your contacts."; }
  if (has("deals page", "deal pipeline", "my deals", "show deals", "show pipeline")) { actions.navigate("deals"); return "Showing your deal pipeline."; }
  if (has("inbox", "email page", "my email", "show email")) { actions.navigate("email"); return "Opening your email."; }
  if (has("dialer page", "open dialer", "power dialer", "start power dial", "let's start dialing", "start dialing", "power dial session", "let's dial", "start calling", "let's start power")) {
    // If on dashboard, open the power dialer panel
    if (window.__coreflow_current_page === "dashboard" && window.__coreflow_open_panel) {
      window.__coreflow_open_panel("dialer");
      return "Starting power dialer on Mission Control. Let's burn through that queue.";
    }
    actions.navigate("dialer");
    return "Opening the power dialer.";
  }
  if (has("task page", "my tasks", "show tasks", "to do")) { actions.navigate("tasks"); return "Showing your tasks."; }
  if (has("analytic", "reports", "numbers page")) { actions.navigate("analytics"); return "Showing analytics."; }
  if (has("setting", "admin", "config")) { actions.navigate("admin"); return "Opening settings."; }
  if (has("workflow", "automation")) { actions.navigate("workflows"); return "Opening workflows."; }
  if (has("companies page", "company page", "show companies")) { actions.navigate("companies"); return "Showing companies."; }
  if (has("sms page", "text page", "messaging")) { actions.navigate("sms"); return "Opening SMS."; }

  // Call
  if (has("call", "dial", "ring", "phone")) {
    const name = extractName();
    if (name.length > 1) {
      const ct = findContact(store.contacts, name);
      if (ct) {
        actions.navigate("dialer");
        return `Loading dialer for ${ct.fn} ${ct.ln}. ${ct.ph || "No phone on file"}.`;
      }
    }
    actions.navigate("dialer");
    return "Opening the power dialer.";
  }

  // Who should I call / what should I do
  if (has("who should i call", "who to call", "who's next", "next call", "who do i call")) {
    const scored = store.contacts
      .filter(c => c.status === "active")
      .map(c => {
        const deal = store.deals.find(d => d.ct === c.id && d.stage !== "Closed Won");
        let priority = c.score || 0;
        if (deal && deal.stage === "Negotiation") priority += 30;
        if (deal && deal.stage === "Proposal") priority += 20;
        if (deal && (deal.risk || "none") === "high") priority += 25;
        return { ...c, priority, deal };
      })
      .sort((a, b) => b.priority - a.priority);
    const top = scored[0];
    if (top) {
      actions.selectContact(top.id);
      return `I'd call ${top.fn} ${top.ln} at ${top.co}. Score ${top.score}${top.deal ? ", " + top.deal.name + " in " + top.deal.stage : ""}. Opening their record.`;
    }
    return "No active contacts to call right now.";
  }

  if (has("what should i do", "what should i work", "what now", "where should i focus", "what's next")) {
    const activeTasks = store.tasks.filter(t => !t.done).sort((a, b) => {
      const pri = { high: 0, medium: 1, low: 2 };
      return (pri[a.pri] || 2) - (pri[b.pri] || 2);
    });
    const risky = store.deals.filter(d => d.stage !== "Closed Won" && ((d.risk || "none") === "high" || d.days > 14));
    let response = "";
    if (risky.length > 0) response += `${risky.length} deal${risky.length > 1 ? "s" : ""} at risk. `;
    if (activeTasks[0]) response += `Top task: ${activeTasks[0].title} (${activeTasks[0].pri}). `;
    if (!response) response = "You're in good shape. No urgent items. ";
    response += `${activeTasks.length} tasks pending.`;
    return response;
  }

  // Pipeline / summary
  if (has("pipeline", "forecast", "summary", "overview", "how are we", "how am i doing", "how's it going", "status update", "give me a summary")) {
    const open = store.deals.filter(d => d.stage !== "Closed Won");
    const pipeline = open.reduce((a, d) => a + d.val, 0);
    const weighted = open.reduce((a, d) => a + d.val * (d.prob / 100), 0);
    const won = store.deals.filter(d => d.stage === "Closed Won");
    const wonVal = won.reduce((a, d) => a + d.val, 0);
    return `${open.length} open deals, ${fmtDollar(pipeline)} pipeline, ${fmtDollar(Math.round(weighted))} weighted. ${won.length} closed won at ${fmtDollar(wonVal)}.`;
  }

  // Deal queries
  if (has("how many deals", "deal count", "total deals", "number of deals")) {
    const open = store.deals.filter(d => d.stage !== "Closed Won");
    const pipeline = open.reduce((a, d) => a + d.val, 0);
    return `${open.length} open deals worth ${fmtDollar(pipeline)}.`;
  }

  if (has("biggest deal", "largest deal", "top deal", "highest value", "most valuable")) {
    const top = store.deals.filter(d => d.stage !== "Closed Won").sort((a, b) => b.val - a.val)[0];
    if (top) {
      const ct = store.contacts.find(c => c.id === top.ct);
      return `Biggest deal: ${top.name} at ${fmtDollar(top.val)}, in ${top.stage}${ct ? " with " + ct.fn + " " + ct.ln : ""}.`;
    }
    return "No open deals found.";
  }

  if (has("at risk", "risky", "stalling", "stuck", "in trouble", "deals at risk")) {
    const risky = store.deals.filter(d => d.stage !== "Closed Won" && ((d.risk || "none") === "high" || d.days > 14));
    if (risky.length > 0) {
      return `${risky.length} deals at risk: ${risky.slice(0, 3).map(d => d.name + ", " + d.days + " days in " + d.stage).join(". ")}.`;
    }
    return "No deals at risk. Pipeline looks clean.";
  }

  if (has("closing soon", "close this month", "about to close")) {
    const soon = store.deals.filter(d => {
      if (d.stage === "Closed Won") return false;
      const daysToClose = d.close ? Math.round((new Date(d.close).getTime() - Date.now()) / 86400000) : 999;
      return daysToClose >= 0 && daysToClose <= 30;
    });
    if (soon.length > 0) return `${soon.length} deals closing within 30 days: ${soon.map(d => d.name + " (" + fmtDollar(d.val) + ")").join(", ")}.`;
    return "No deals closing within 30 days.";
  }

  // Contact count
  if (has("how many contacts", "contact count", "total contacts", "number of contacts")) {
    const active = store.contacts.filter(c => c.status === "active");
    return `${store.contacts.length} total contacts, ${active.length} active.`;
  }

  // Top contacts
  if (has("top contacts", "top leads", "best leads", "hottest leads")) {
    const top = [...store.contacts].sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 3);
    return `Top 3: ${top.map(c => c.fn + " " + c.ln + " (" + (c.score || 0) + " pts, " + c.co + ")").join(". ")}.`;
  }

  // Tasks
  if (has("next task", "top task", "my task", "pending task")) {
    const activeTasks = store.tasks.filter(t => !t.done).sort((a, b) => {
      const pri = { high: 0, medium: 1, low: 2 };
      return (pri[a.pri] || 2) - (pri[b.pri] || 2);
    });
    if (activeTasks.length === 0) return "No pending tasks. You're all caught up.";
    const top = activeTasks[0];
    return `Top task: ${top.title}. ${top.pri} priority, due ${top.due}. ${activeTasks.length} total tasks.`;
  }

  // Quota
  if (has("quota", "target", "attainment", "on track", "on pace")) {
    const won = store.deals.filter(d => d.stage === "Closed Won").reduce((a, d) => a + d.val, 0);
    const me = store.teamMembers[0];
    const pct = me?.quota ? Math.round(won / me.quota * 100) : 0;
    return `Closed ${fmtDollar(won)}.${me?.quota ? " That's " + pct + "% of " + fmtDollar(me.quota) + " quota." : " No quota set."}`;
  }

  // Team
  if (has("team", "who's on my", "team members")) {
    if (store.teamMembers.length === 0) return "No team members loaded.";
    return `Your team: ${store.teamMembers.map(m => m.name + " (" + m.role + ")").join(", ")}.`;
  }

  // Contact lookup — expanded patterns
  if (has("show", "open", "find", "pull up", "look up", "tell me about", "who is", "info on", "details on", "breakdown", "what about", "everything on", "what do you know")) {
    const name = extractName();
    if (name.length > 1) {
      const ct = findContact(store.contacts, name);
      if (ct) {
        actions.selectContact(ct.id);
        const deal = store.deals.find(d => d.ct === ct.id && d.stage !== "Closed Won");
        return `${ct.fn} ${ct.ln}, ${ct.title || "No title"} at ${ct.co || "Unknown"}. Score: ${ct.score || 0}. ${deal ? "Active deal: " + deal.name + " (" + fmtDollar(deal.val) + ", " + deal.stage + ")" : "No active deals"}.`;
      }
    }
  }

  // ── FUZZY FALLBACK: try to match a contact name anywhere in the transcript ──
  const words = t.split(" ").filter(w => w.length > 2);
  for (let i = 0; i < words.length - 1; i++) {
    const twoWords = words[i] + " " + words[i + 1];
    const ct = findContact(store.contacts, twoWords);
    if (ct) {
      actions.selectContact(ct.id);
      const deal = store.deals.find(d => d.ct === ct.id && d.stage !== "Closed Won");
      return `Found ${ct.fn} ${ct.ln} — ${ct.title || ""} at ${ct.co || ""}. Score: ${ct.score || 0}. ${deal ? deal.name + " in " + deal.stage : "No active deal"}. Opening their record.`;
    }
  }

  return null; // No local match — will try Claude
}

/* ── VOICE COMMAND COMPONENT ── */
export default function VoiceCommand({ onNavigate, onSelectContact, onSelectDeal }) {
  const store = useStore();
  const auth = useAuth();
  // Dashboard check — must be declared before any conditional logic but after hooks
  const [onDashboard, setOnDashboard] = useState(window.__coreflow_current_page === "dashboard");
  useEffect(() => {
    const check = () => setOnDashboard(window.__coreflow_current_page === "dashboard");
    check();
    const interval = setInterval(check, 300);
    return () => clearInterval(interval);
  }, []);

  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [response, setResponse] = useState("");
  const [showPanel, setShowPanel] = useState(false);
  const [history, setHistory] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [mode, setMode] = useState("voice"); // voice | text
  const [textInput, setTextInput] = useState("");
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);

  // Load voices
  useEffect(() => {
    synth?.getVoices();
    const onVoices = () => synth?.getVoices();
    synth?.addEventListener?.("voiceschanged", onVoices);
    return () => synth?.removeEventListener?.("voiceschanged", onVoices);
  }, []);

  const actions = {
    navigate: (page) => onNavigate(page),
    selectContact: (id) => onSelectContact(id),
    selectDeal: (id) => onSelectDeal(id),
  };

  // Execute actions from Claude's response
  const executeActions = (actionList) => {
    (actionList || []).forEach(a => {
      if (a.type === "navigate") actions.navigate(a.value);
      else if (a.type === "contact") actions.selectContact(a.value);
      else if (a.type === "deal") actions.selectDeal(a.value);
    });
  };

  // Process a command — try local first, then Claude
  const processCommand = useCallback(async (text) => {
    // Mission Control handles its own commands
    if (window.__coreflow_current_page === "dashboard") return;
    // 1. Try local fast parsing
    const localResult = parseCommandLocal(text, store, actions);
    if (localResult) {
      setResponse(localResult);
      setHistory(prev => [...prev, { q: text, a: localResult, time: new Date().toLocaleTimeString(), source: "local" }].slice(-20));
      speak(localResult);
      return;
    }

    // 2. Call Claude API
    setThinking(true);
    try {
      const resp = await fetch("/api/ai/specialty", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "simple_chat",
          command: text,
          conversationHistory: history.slice(-6),
          orgId: auth.currentUser?._orgId,
          context: {
            contacts: store.contacts.slice(0, 25),
            deals: store.deals,
            tasks: store.tasks,
            pipeline: {
              total: store.deals.filter(d => d.stage !== "Closed Won").reduce((a, d) => a + d.val, 0),
              weighted: store.deals.filter(d => d.stage !== "Closed Won").reduce((a, d) => a + d.val * (d.prob / 100), 0),
              wonYTD: store.deals.filter(d => d.stage === "Closed Won").reduce((a, d) => a + d.val, 0),
            },
            team: store.teamMembers,
          },
        }),
      });
      const data = await resp.json();
      if (data.success) {
        const modelTag = data.model === "sonnet" ? " ✦" : "";
        setResponse(data.reply);
        executeActions(data.actions);
        setHistory(prev => [...prev, { q: text, a: data.reply, time: new Date().toLocaleTimeString(), source: "ai", model: data.model }].slice(-20));
        speak(data.reply);
      } else if (data.limitReached) {
        const msg = data.error || "AI limit reached for this month. Upgrade for more.";
        setResponse(msg);
        speak(msg);
      } else {
        const fallback = `I heard "${text}" but couldn't process that. Try "show my pipeline" or "who should I call next".`;
        setResponse(fallback);
        speak(fallback);
      }
    } catch (e) {
      console.warn("[Core] AI call failed:", e.message);
      const fallback = `I heard "${text}". My AI brain is offline — try again or say "help" for commands.`;
      setResponse(fallback);
      speak(fallback);
    }
    setThinking(false);
  }, [store, history]);

  const startListening = useCallback(() => {
    if (window.__coreflow_current_page === "dashboard") return;
    if (!SpeechRecognition) {
      setResponse("Speech recognition not supported. Use Chrome.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);

    recognition.onresult = (event) => {
      let final = "";
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) final += event.results[i][0].transcript;
        else interim += event.results[i][0].transcript;
      }
      setTranscript(final || interim);
      if (final) processCommand(final);
    };

    recognition.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed") setResponse("Microphone access denied. Allow mic access and try again.");
      else setResponse("Error: " + e.error);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setShowPanel(true);
  }, [processCommand, onDashboard]);

  const stopListening = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  // Text input handler
  const handleTextSubmit = (e) => {
    e?.preventDefault();
    if (!textInput.trim()) return;
    setTranscript(textInput);
    processCommand(textInput.trim());
    setTextInput("");
  };

  // Keyboard shortcut: hold space to talk
  useEffect(() => {
    const down = (e) => {
      // Never capture keyboard on Mission Control — it has its own handler
      if (window.__coreflow_current_page === "dashboard") return;
      if (e.code === "Space" && !["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) && !listening) {
        e.preventDefault();
        startListening();
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [listening, startListening]);

  // Focus text input when switching to text mode
  useEffect(() => {
    if (mode === "text" && showPanel) setTimeout(() => inputRef.current?.focus(), 100);
  }, [mode, showPanel]);

  const userName = auth.currentUser?.firstName || "there";

  // Hide on Mission Control
  if (onDashboard) return null;

  return (
    <>
      {/* Mic button — inline for top bar */}
      <button onClick={() => { if (listening) stopListening(); else if (!showPanel) { setShowPanel(true); } else { setShowPanel(false); } }}
        style={{
          position: "relative", width: 32, height: 32, borderRadius: 8,
          background: listening ? "#E53935" : thinking ? "#4059AD" : "transparent",
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.2s", zIndex: 9999,
          animation: listening ? "corePulse 1.5s ease infinite" : thinking ? "corePulse 1s ease infinite" : "none",
        }}>
        <Icon name={listening ? "pause" : thinking ? "ai" : "mic"} size={15} color={listening || thinking ? "#fff" : T.tx3} />
      </button>

      {/* Command palette */}
      {showPanel && (
        <div style={{
          position: "fixed", top: 56, right: 24, width: 380, maxHeight: 500,
          background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 16,
          boxShadow: "0 40px 100px rgba(0,0,0,0.40)",
          zIndex: 9998, display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          {/* Input */}
          <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(217,219,221,0.08)", display: "flex", gap: 8, alignItems: "center" }}>
            {mode === "voice" && listening ? (
              <>
                <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: "#E53935", animation: "corePulse 1.5s ease infinite", flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: T.txt, fontStyle: transcript ? "normal" : "italic", opacity: transcript ? 1 : 0.5 }}>{transcript || "Listening..."}</span>
                </div>
                <button onClick={stopListening} style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: "#E53935", color: "#fff", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Stop</button>
              </>
            ) : (
              <>
                <input
                  ref={inputRef}
                  value={textInput} onChange={e => setTextInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleTextSubmit(); }}
                  placeholder="Ask anything..."
                  autoFocus
                  style={{
                    flex: 1, padding: "8px 12px", borderRadius: 10, border: "1px solid rgba(217,219,221,0.12)",
                    background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 12, outline: "none", fontFamily: "inherit",
                  }}
                />
                <button onClick={() => { if (mode === "voice") startListening(); else handleTextSubmit(); }} disabled={thinking} style={{
                  width: 32, height: 32, borderRadius: 8, border: "none", flexShrink: 0,
                  background: thinking ? "rgba(255,255,255,0.06)" : "#4059AD",
                  cursor: thinking ? "default" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Icon name={mode === "voice" ? "mic" : "send"} size={14} color="#fff" />
                </button>
                <button onClick={() => setMode(mode === "voice" ? "text" : "voice")} style={{
                  width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", flexShrink: 0,
                  background: "rgba(255,255,255,0.06)", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Icon name={mode === "voice" ? "keyboard" : "mic"} size={13} color={T.tx3} />
                </button>
                <button onClick={() => setShowPanel(false)} style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.tx3, flexShrink: 0 }}>\u00d7</button>
              </>
            )}
          </div>

          {/* Response */}
          {(thinking || response) && (
            <div style={{ padding: "14px 16px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
              {thinking ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ display: "flex", gap: 3 }}>{[0,1,2].map(i => <div key={i} style={{ width: 4, height: 4, borderRadius: 2, background: T.tx3, animation: `dotPulse 1.2s ease ${i*0.2}s infinite` }} />)}</div>
                  <span style={{ fontSize: 11, color: T.tx3 }}>Processing...</span>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: T.txt, lineHeight: 1.6 }}>{response}</div>
              )}
            </div>
          )}

          {/* History & suggestions */}
          <div style={{ flex: 1, overflow: "auto", maxHeight: 320 }}>
            {history.slice().reverse().map((h, i) => (
              <div key={i} style={{ padding: "10px 16px", borderBottom: "1px solid rgba(217,219,221,0.06)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.txt, marginBottom: 3 }}>{h.q}</div>
                <div style={{ fontSize: 11, color: T.tx3, lineHeight: 1.5 }}>{h.a}</div>
              </div>
            ))}
            {history.length === 0 && !response && !thinking && (
              <div style={{ padding: "8px 14px" }}>
                {["Who should I call next?", "Pipeline summary", "Draft a follow-up email for Sarah", "Deals at risk", "What should I work on today?"].map((cmd, i) => (
                  <button key={i} onClick={() => { processCommand(cmd); }}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", margin: "1px 0",
                      borderRadius: 8, border: "none", background: "transparent", color: T.tx2, fontSize: 11,
                      cursor: "pointer", fontFamily: "inherit" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {cmd}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* CSS animations */}
      <style>{`
        @keyframes corePulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.06); opacity: 0.85; }
        }
        @keyframes barBounce {
          0% { height: 4px; }
          100% { height: 22px; }
        }
        @keyframes dotPulse {
          0%, 100% { transform: scale(0.7); opacity: 0.3; }
          50% { transform: scale(1.3); opacity: 1; }
        }
        @keyframes dotGlow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}
